import React, { useContext, useState, useMemo } from 'react';
import { Container, Row, Col, Button, Table, Form, Modal } from 'react-bootstrap';
import DateSelectorModal from './components/modals/modal-date-selector';
import ReportsService from '../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../context/identity-context';

const AuditConfirmation = ({ auditConfirmationReportsResponse }) => {
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [reportData, setReportData] = useState([]);
  const { user } = useContext(IdentityContext);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Download confirmation states
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadTarget, setDownloadTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const reportService = new ReportsService();

  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  const existingTodayCount = useMemo(() => {
    const existingDates = [
      ...(auditConfirmationReportsResponse || []).map(r => r.createdDate),
      ...reportData.map(r => r.date)
    ];
    return existingDates.filter(dateStr => dateStr === today).length;
  }, [auditConfirmationReportsResponse, reportData, today]);

  const isLimitReached = existingTodayCount >= 2;

  const handlePreviewClick = async (report) => {
    try {
      const response = await reportService.retrieveReport(
        report.reportType,
        report.issuerName,
        report.reportTitle,
        report.issuerRequestDate
      );
      const newWindow = window.open(
        response.fileURL,
        "_blank",
        "noopener,noreferrer"
      );
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error("Error fetching report preview:", error.message);
    }
  };

  // Open confirmation dialog
  const confirmDownload = (report) => {
    setDownloadTarget(report);
    setShowDownloadModal(true);
  };

  const executeDownload = async () => {
    if (!downloadTarget) return;
    setDownloading(true);
    let success = false;
    try {
      const response = await reportService.issuerDownloadAuditConfirmation(
        user.attributes['custom:entity_id'],
        downloadTarget.issuerRequestDate || downloadTarget.date,
        user.attributes['custom:display_name']
      );

      const url = response.fileURL;
      const filename = response.fileName || url.substring(url.lastIndexOf('/') + 1);

      const fileResponse = await fetch(url);
      if (!fileResponse.ok) throw new Error('Network response was not ok');

      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
      success = true;
    } catch (error) {
      console.error('Error fetching report download:', error.message || error);
    } finally {
      setDownloading(false);
      setShowDownloadModal(false);
      if (success) {
        window.location.reload();
      }
    }
  };

  const handleOpenDateModal = () => setShowDateModal(true);
  const handleCloseDateModal = () => setShowDateModal(false);

  const auditConfirmationReports = auditConfirmationReportsResponse || [];

  const handleConfirmDates = (dates) => {
    setSelectedDates(prevDates => {
      const newDates = [...new Set([...prevDates, ...dates])];
      return newDates;
    });

    const allExistingDates = [
      ...auditConfirmationReports.map(r => r.issuerRequestDate),
      ...reportData.map(r => r.date)
    ];

    const validDates = dates.filter(
      date => date && date.trim() && !allExistingDates.includes(date)
    );

    const newReports = validDates.map(date => ({ date, status: '-' }));
    setReportData(prev => [...prev, ...newReports]);
    setShowDateModal(false);
  };

  const handleCheckboxChange = date => {
    setSelectedReports(prev =>
      prev.some(sel => sel.date === date)
        ? prev.filter(sel => sel.date !== date)
        : [...prev, { date }]
    );
  };

  const handleRequestReports = async () => {
    try {
      const reportsToSend = selectedReports.map(r => ({
        institutionId: user.attributes['custom:entity_id'],
        issuer: user.attributes['email'],
        issuerName: user.attributes['custom:institution_name'],
        issuerRequestDate: r.date,
        reportTitle: 'Audit Confirmation'
      }));

      await reportService.issuerRequestAdhocReport(reportsToSend);
      setShowSuccessModal(true);
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      console.error('Error requesting reports:', error);
      setErrorMessage(
        error.response?.data?.message || error.message || 'Unknown error'
      );
      setShowErrorModal(true);
    }
  };

  return (
    <Container className="p-4 border rounded shadow-sm report-container">
      {/* Header and Select Date button */}
      <Row className="d-flex justify-content-between align-items-center mb-3 w-100">
        <Col><h4>Audit Confirmation</h4></Col>
        <Col className="d-flex justify-content-end">
          <Button variant="primary" onClick={handleOpenDateModal} disabled={isLimitReached}>
            Select Date
          </Button>
        </Col>
      </Row>

      {/* Reports Table */}
      <Table bordered hover className="mt-3">
        <thead>
          <tr>
            <th style={{ width: '1%' }}></th>
            <th>Data as of</th>
            <th>Status</th>
            <th style={{ width: '1%' }} className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {auditConfirmationReports.length === 0 && reportData.length === 0 ? (
            <tr><td colSpan="4" className="text-center">No reports</td></tr>
          ) : (
            [...auditConfirmationReports, ...reportData].map((report, idx) => {
              const dateKey = report.issuerRequestDate || report.date;
              return (
                <tr key={idx}>
                  <td style={{ width: '1%' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedReports.some(sel => sel.date === dateKey)}
                      onChange={() => handleCheckboxChange(dateKey)}
                      disabled={report.status !== '-'}
                    />
                  </td>
                  <td>{dateKey}</td>
                  <td>{report.status}</td>
                  <td className="text-center" style={{ width: '1%' }}>
                    <Button
                      variant="primary"
                      size="sm"
                      className="me-2 mb-2"
                      onClick={() => handlePreviewClick(report)}
                      disabled={report.status !== 'Report Available'}
                    >Preview</Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => confirmDownload(report)}
                      disabled={report.status !== 'Report Available'}
                    >Download</Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </Table>

      {/* Request Reports Button */}
      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={handleRequestReports}
            disabled={selectedReports.length === 0 || isLimitReached}
          >Request for Report</Button>
        </Col>
      </Row>

      {/* Date Selector Modal */}
      <DateSelectorModal
        show={showDateModal}
        onClose={handleCloseDateModal}
        onConfirm={handleConfirmDates}
        onDateChange={(dates) => {
        console.log('dates changed:', dates);
          setSelectedDates(dates); }}
      />

      {/* Success & Error Modals */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
        <Modal.Header closeButton><Modal.Title>Success</Modal.Title></Modal.Header>
        <Modal.Body>Reports requested successfully!</Modal.Body>
      </Modal>

      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)}>
        <Modal.Header closeButton><Modal.Title>Error</Modal.Title></Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
      </Modal>

      {/* Download Confirmation Modal */}
      <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Warning: Authorized Access Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            By proceeding to download this report, you confirm that you are an authorized individual
            with permission to access this sensitive information.
          </p>
          <p>
            Once downloaded, the file will no longer be available in the portal. Ensure you have
            the necessary permissions and are ready before continuing.
          </p>
          <p>Do you wish to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDownloadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={executeDownload}
            disabled={downloading}
          >
            {downloading ? 'Downloading…' : 'Continue'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AuditConfirmation;
